import React, { createContext, useState, useContext, useEffect } from "react";
import {
  useCognito,
  getAuth,
  getAuthHeaders,
  getCurrentUserEmail,
  getCurrentUser,
} from "../services/authUtils";

export const AuthContext = createContext();

export const useAuth = () => useContext(AuthContext);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authProvider, setAuthProvider] = useState(null);

  useEffect(() => {
    let unsubscribe = () => {};
    let authCheckInterval = null;

    if (useCognito) {
      // Initial check for Cognito user
      const checkCognitoAuth = async () => {
        try {
          const cognitoUser = await getCurrentUser().catch((err) => {
            if (err.name === "UserUnAuthenticatedException") {
              return null;
            }
            throw err;
          });

          if (cognitoUser) {
            setUser({
              ...cognitoUser,
              email: cognitoUser?.attributes?.email,
              emailVerified: true,
              displayName:
                `${cognitoUser?.attributes?.given_name || ""} ${cognitoUser?.attributes?.family_name || ""}`.trim(),
              authProvider: "cognito",
            });
            setAuthProvider("cognito");
          } else {
            setUser(null);
            setAuthProvider(null);
          }
        } catch (error) {
          console.error("Error checking Cognito auth:", error);
          setUser(null);
          setAuthProvider(null);
        }

        setLoading(false);
      };

      // Check immediately and set up periodic check
      checkCognitoAuth();

      // Setup polling for Cognito auth state changes
      authCheckInterval = setInterval(checkCognitoAuth, 30000);
    } else {
      // Firebase auth remains unchanged
      const firebaseAuth = getAuth();
      unsubscribe = firebaseAuth.onAuthStateChanged((firebaseUser) => {
        if (firebaseUser) {
          setUser({ ...firebaseUser, authProvider: "firebase" });
          setAuthProvider("firebase");
        } else {
          setUser(null);
          setAuthProvider(null);
        }
        setLoading(false);
      });
    }

    return () => {
      unsubscribe();
      if (authCheckInterval) clearInterval(authCheckInterval);
    };
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user,
        loading,
        authProvider,
        useCognito,
        auth: getAuth(),
        getAuthHeaders,
        getCurrentUserEmail,
        getCurrentUser,
        setUser,
      }}
    >
      {!loading && children}
    </AuthContext.Provider>
  );
};
